import { useContext, createContext, useState, useEffect } from "react";
import { createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updateProfile, User, UserCredential } from "firebase/auth";
import { auth } from "./FirebaseConfig";
import { CreateTeacherUser } from "./FirebaseQueries";


type ContextType = {
	currentUser: User | null;
	currentRole: string;
	isLoading: boolean;
	isLoggingIn: boolean;
	setIsLoggingIn(value: boolean): void;
	login(email: string, password: string): Promise<UserCredential>;
	logout(): void;
	signup(email: string, password: string, fullName: string): void;
	finishSignup(user: User, fullName: string, email: string): void;
	resetPassword(email: string): void;
	verifyEmail(): void;
	SetCurrentRole(value: string): void;
	GetCurrentRole(): string;
}

const AuthContext = createContext<ContextType>({
	currentUser: null,
	currentRole: "",
	isLoading: true,
	isLoggingIn: false,
	setIsLoggingIn: (): void => { },
	login: (): Promise<UserCredential> => {
		return new Promise<UserCredential>(() => { });
	},
	logout: (): void => { },
	signup: (): void => { },
	finishSignup: (): void => { },
	resetPassword: (): void => { },
	verifyEmail: (): void => { },
	SetCurrentRole: (): void => { },
	GetCurrentRole: (): string => { return "" },
});

export const useAuth = () => {
	return useContext(AuthContext)
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const [currentRole, setCurrentRole] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);

	function login(email: string, password: string): Promise<UserCredential> {
		setIsLoggingIn(true);
		return signInWithEmailAndPassword(auth, email, password);
	}

	function logout() {
		return signOut(auth);
	}

	async function signup(email: string, password: string, fullName: string) {
		createUserWithEmailAndPassword(auth, email, password).then((userCred) => {
			// Auth profile is displayName
			updateProfile(userCred.user, {
				displayName: fullName
			})
			CreateTeacherUser(userCred.user.uid, fullName, email);
		}).catch((error) => {
			// const errorCode = error.code;
			const errorMessage: string = error.message;
		});
	}

	function finishSignup(user: User, fullName: string, email: string) {
		// Auth profile is displayName
		updateProfile(user, {
			displayName: fullName
		})
		CreateTeacherUser(user.uid, fullName, email);
	}

	function resetPassword(email: string) {
		sendPasswordResetEmail(auth, email).then(() => {
			// Password reset email sent!
			console.log("Password reset email sent!")
		})
			.catch((error) => {
				console.log(error.code);
				console.log(error.message);
			});
	}

	function verifyEmail() {
		if (currentUser === null) return;

		sendEmailVerification(currentUser).then(() => {
			console.log("Verification email sent to: " + currentUser.email);
		}).catch((error) => {
			console.log(error.code);
			console.log(error.message);
		});
	}

	function SetCurrentRole(value: string): void {
		setCurrentRole(value);
		localStorage.setItem("currentRole", value);
	}

	function GetCurrentRole(): string {
		let role = localStorage.getItem("currentRole");
		return role !== null ? role : "";
	}

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged(user => {
			setCurrentUser(user);
			setIsLoading(false);
			setIsLoggingIn(false);
			setCurrentRole(GetCurrentRole());
		})

		return unsubscribe;
	}, [])

	const value: ContextType = {
		currentUser,
		currentRole,
		isLoading,
		isLoggingIn,
		setIsLoggingIn,
		login,
		logout,
		signup,
		finishSignup,
		resetPassword,
		verifyEmail,
		SetCurrentRole,
		GetCurrentRole
	}

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	)
}